import React, {FunctionComponent, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useIntl} from 'react-intl';

import {loadArticleAction} from 'redux/content/contentAsyncActions';
import {IStore} from 'redux/interface';

import {
  BotActionType,
  BotChat as Chat,
  BotMode,
  BotNameMode,
  BotType,
  ButtonVariant,
  GritxButton,
  IBotAction, IVideo,
  MessageType
} from '@wholesalechange/chatcomponent';
import BotIcon from 'assets/image/user-settings/BotDefaultAvatar.png';
import {useLoadFile} from 'utils/hooks/useLoadFile';

import {XpeditionList} from './xpedition/XpeditionList';
import {BotContent} from './BotContent';
import {
  contentAsyncActions,
  dialogueAsyncActions,
  getArticle,
  getDialogueParticipant,
  getXpeditions
} from '../../redux/pageAsyncActions';
import {useDictionary} from '../../utils/hooks/useDictionary';
import {useChatBotNew} from '../../utils/hooks/useChatBotNew';
import Loader from '../loader';

import './styles.scss';
import {useNavigate} from 'react-router-dom';
import NotFoundInfo from '../not-found-info';
import CrossroadImg from '../../assets/image/library/skill-crossroad.svg';
import {closeArticle} from '../../redux/content/actions';
import {useUploadImage} from '../../utils/hooks/useUploadImage';
import {useUploadVideo} from '../../utils/hooks/useUploadVideo';
import {FileWithPath} from 'react-dropzone';

interface IBotChat {
  chatType: BotType
  chatMode: BotMode
  dialogueId?: number
  title: string
  tabsEnabled: boolean
}

export const BotChat: FunctionComponent<IBotChat> = ({chatType, chatMode, dialogueId, title, tabsEnabled}: IBotChat) => {
  const intl = useIntl();

  const [isShowContent, setIsShowContent] = useState(true);
  const [contentTitle, setContentTitle] = useState<string>('');
  const [toggleChatTab, setToggleChatTab] = useState(false);
  const [botAvatar, setBotAvatar] = useState('');

  const chatBot = useChatBotNew(chatType, chatMode);
  const history = useNavigate();

  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    article
  } = useSelector((state: IStore) => state.content);
  const {
    userProfile
  } = useSelector((state: IStore) => state.auth);
  const {
    participant, xpeditions
  } = useSelector((state: IStore) => state.dialogue);
  const {settings} = useSelector((state: IStore) => state.auth);

  const dictionary = useDictionary();

  const dispatch = useDispatch();
  const {getFileUrl, getFileUrlAsync, getVideoUrlAsync, removeFileAsync} = useLoadFile();
  const {uploadImageUrlAsync} = useUploadImage();
  const {uploadVideoAsync} = useUploadVideo();

  useEffect(() => {
    if (chatBot.currentDialog && userProfile) {
      dispatch(dialogueAsyncActions[getDialogueParticipant](userProfile.id, chatBot.currentDialog.id));
    }
  }, [userProfile, chatBot.currentDialog]);

  useEffect(() => {
    if ((chatType === BotType.Xpedition || chatType === BotType.Dialogue) && dialogueId) {
      chatBot.xpedition.setXpeditionDialogue(dialogueId);
    }
  }, [dialogueId]);

  useEffect(() => {
    if (article && article.title) {
      setContentTitle(article.title);
    } else if (chatBot.botAnswer && chatBot.botAnswer.length) {
      const pictures = chatBot.botAnswer.filter(item => {
        return !item.contentId && item.picture;
      });

      if (pictures && pictures.length && pictures[0].text) {
        setContentTitle(pictures[0].text);
      } else {
        setContentTitle('');
      }
    } else {
      setContentTitle('');
    }
  }, [chatBot.botAnswer, article]);

  const pushUserMessage = (text: string) => {
    if (chatBot.currentDialog && participant) {
      chatBot.sendMessage(MessageType.Text, participant, {text});
    }
  };

  const handleSendMessage = (message: string) => {
    pushUserMessage(message.trim());
  };

  async function loadMore() {
    if (chatBot.currentDialog && !chatBot.isLoading) {
      await chatBot.dialogueMethods.fetchMore();
    }
  }

  function handleShowArticle(code: string) {
    setIsShowContent(true);
    if (article && article.id.toString() === code) {
      return;
    }
    dispatch(contentAsyncActions[getArticle](code, false));
  }

  function handleCloseArticle() {
    if (article) {
      dispatch(closeArticle());
      setIsShowContent(false);
      setContentTitle('');
    }
  }

  function handleCloseContent() {
    setToggleChatTab(true);
    setTimeout(() => {
      setToggleChatTab(false);
    }, 1000);
  }

  function handleShowExternalOverlay() {
    if (article) {
      dispatch(closeArticle());
    }
    setIsShowContent(false);
  }

  useEffect(() => {
    if (chatType === BotType.XpeditionList) {
      dispatch(dialogueAsyncActions[getXpeditions]());
    } else if (chatType !== BotType.Xpedition && chatType !== BotType.Dialogue && userProfile?.greetingContentId) {
      dispatch(loadArticleAction(userProfile.greetingContentId.toString(), false));
      setIsShowContent(true);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile) {
      return;
    }
    if (userProfile.dialogAvatarId) {
      getFileUrl(userProfile.dialogAvatarId, (url: string | null) => {
        if (url) {
          setBotAvatar(url);
        }
      });
    } else {
      setBotAvatar(BotIcon);
    }
  }, [userProfile]);

  function handleHelp() {
    pushUserMessage(intl.formatMessage({
      id: 'gritx.chat.helpButton',
      defaultMessage: 'Help'
    }));
  }

  function handleSendAction(actionButton: IBotAction) {
    if (!chatBot.currentDialog || !participant) {
      return;
    }
    if (!actionButton) {
      return;
    }
    const actionBody = JSON.parse(actionButton.actionBody);

    switch (actionButton.actionType) {
      case BotActionType.ContentView:
        dispatch(loadArticleAction(actionBody.addActionBody.contentInfo.id, false));
        setIsShowContent(true);
        chatBot.sendMessage(MessageType.Action, participant, {
          button: {
            actionType: actionButton.actionType,
            actionBody: actionButton.actionBody,
            actionUrl: actionButton.actionUrl,
            serialNumber: actionButton.serialNumber,
            text: actionButton.text
          }
        });
        break;
      case BotActionType.OpenURL:
      case BotActionType.ReturnValue:
      case BotActionType.Schedule:
      case BotActionType.Image:
      case BotActionType.Overlay:
        chatBot.sendMessage(MessageType.Action, participant, {
          button: {
            actionType: actionButton.actionType,
            actionBody: actionButton.actionBody,
            actionUrl: actionButton.actionUrl,
            serialNumber: actionButton.serialNumber,
            text: actionButton.text
          }
        });
        break;
      default:
        break;
    }
  }

  function handleStartXpedition(botId: number) {
    history(`/xpedition/${botId}`);
  }

  async function handleRemoveDialog(dialogId: number) {
    await chatBot.dialogueMethods.removeDialog(dialogId);
    dispatch(dialogueAsyncActions[getXpeditions]());
  }

  function uploadDialogVideoAsync(dialogId: number) {
    return (file: FileWithPath, type: string): Promise<IVideo> => {
      return uploadVideoAsync(file, dialogId, type);
    };
  }

  function renderButtons() {
    return chatType !== BotType.Xpedition ? <GritxButton key='help'
      title={intl.formatMessage({
        id: 'gritx.chat.helpButton',
        defaultMessage: 'Help'
      })}
      variant={ButtonVariant.Outline}
      className="dialogue-chat__help-btn"
      onClick={handleHelp}
    /> : <React.Fragment key="none"></React.Fragment>;
  }

  function getShowContentInRightPanel() {
    return chatType === BotType.Xpedition || chatBot.showContentInRightPanel;
  }

  function isLoading() {
    if (chatType === BotType.XpeditionList) {
      return loading.includes(getXpeditions);
    }

    return !chatBot || (!chatBot.currentDialog && !chatBot.dialogIsNotSet);
  }

  if (isLoading()) {
    return <Loader nested/>;
  }

  if (chatType === BotType.XpeditionList) {
    if (xpeditions.length === 0) {
      return <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.xpedition.notFound',
          defaultMessage: 'No Xpeditions Added Yet'
        })}
        image={CrossroadImg}
      />;
    }

    return <XpeditionList
      onStart={handleStartXpedition}
      onRemove={handleRemoveDialog}
    />;
  }

  if (chatBot.dialogIsNotSet) {
    return <NotFoundInfo
      title={intl.formatMessage({
        id: 'gritx.chat.notFound',
        defaultMessage: 'Dialogue has not been added yet'
      })}
      image={CrossroadImg}
    />;
  }

  if (chatBot.currentDialog) {
    return <>
      <div className="chat__container">
        {userProfile && <Chat isLoading={chatBot.isLoading}
          isTyping={chatBot.isTyping}
          isSending={chatBot.isSending}
          chatAvatar={botAvatar}
          toolbarButtons={[renderButtons()]}
          messages={chatBot.messages || []}
          onSend={handleSendMessage}
          onSendAction={handleSendAction}
          currentUser={userProfile}
          onLoadMore={loadMore}
          onShowArticle={handleShowArticle}
          onCloseArticle={handleCloseArticle}
          onShowExternalOverlay={handleShowExternalOverlay}
          dictionary={dictionary}
          botMode={chatMode}
          isConnected={chatBot.isConnected}
          botNameMode={BotNameMode.PARTICIPANT}
          getFileUrl={getFileUrlAsync}
          getVideoUrl={getVideoUrlAsync}
          removeFile={removeFileAsync}
          uploadImage={uploadImageUrlAsync}
          uploadVideo={uploadDialogVideoAsync(chatBot.currentDialog.id)}
          ttsSettings={{
            azureSubscriptionKey: process.env.REACT_APP_AZURE_SUBSCRIPTION_KEY || '',
            azureRegion: process.env.REACT_APP_AZURE_REGION || '',
            excludeButtons: settings?.chatSettings?.excludedButtons || []
          }}
          showContentInRightPanel={getShowContentInRightPanel()}
          contentElement={isShowContent ? <BotContent botAnswer={chatBot.botAnswer} onCloseContent={handleCloseContent}/> : null}
          tabsEnabled={tabsEnabled}
          dialogueTitle={title}
          contentTitle={contentTitle}
          toggleChatTab={toggleChatTab}
        />}
      </div>
    </>;
  }

  return <></>;
};
